import { useQuery, useMutation } from '@tanstack/react-query'
import { getMerchantLocation, getMerchantLocations, getActiveLocations, updateActiveLocations } from '../../api/merchant.api'
import { queryClient } from '@/providers/QueryProvider'
import { useActiveLocationsStore } from '../useActiveLocationsStore'
import { IS_HUB_PREVIEW } from '@/preview/hubPreview'
import { applyHubPreviewHeaders } from '@/preview/hubPreviewApi'

export const useMerchantLocation = () => {
  return useQuery({
    queryKey: ['merchant', 'location'],
    queryFn: () => getMerchantLocation(),
  })
}

export const useMerchantLocations = () => {
  return useQuery({
    queryKey: ['merchant', 'locations'],
    queryFn: () => getMerchantLocations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useActiveLocations = () => {
  return useQuery({
    queryKey: ['merchant', 'active-locations'],
    queryFn: () => getActiveLocations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useUpdateActiveLocationsMutation = () => {
  return useMutation({
    mutationFn: (locationIds: string[]) => {
      const { availableLocations } = useActiveLocationsStore.getState();
      
      // Convert "all selected" to empty array for backend
      const backendLocationIds = locationIds.length === availableLocations.length 
        ? [] 
        : locationIds;
      
      // The Hub preview never saves: the selection rides on each request instead.
      if (IS_HUB_PREVIEW) {
        applyHubPreviewHeaders(backendLocationIds);
        return Promise.resolve();
      }

      return updateActiveLocations(backendLocationIds);
    },
    onSuccess: (_, locationIds) => {
      // Optimistically update the store with new location IDs
      const { availableLocations, setActiveLocations } = useActiveLocationsStore.getState();
      setActiveLocations(locationIds, availableLocations);
      
      // Invalidate active locations query to refetch
      queryClient.invalidateQueries({ queryKey: ['merchant', 'active-locations'] })
      // Invalidate list queries to refetch with new location filter
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['member'] })
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['website-leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
